import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  FloatingLabel,
  Form,
  Image,
  Row,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import CurrencyInput from "../../../../../components/common/CurrencyInput";
import { commonStrings } from "../../../../../globalization";
import { ProductControllerImpl } from "../../../business/impl/controllers";
import { AdmPanelContainer } from "../../../../adm-panel/site/api/screens";
import { useAuth } from "../../../../../providers";
import { UserDataPanelWrapper } from "../../../../account";

export function ProductCreate({ navigate }) {
  const { setCallbackLogin, handleLogout, userData } = useAuth();
  const translate = commonStrings().PTBR.dashboard.auto_part.create;
  const controller = new ProductControllerImpl();
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [price, setPrice] = useState();
  const [toasting, setToasting] = useState(null);
  const [isProccessing, setProccessing] = useState(false);

  const [formData, setFormData] = useState({
    partName: "",
    manufacturer: "",
    brand: "",
    model: "",
    isNew: 0,
    isNational: 1,
    description: "",
    quantity: "",
    code: "",
  });
  const {
    partName,
    manufacturer,
    brand,
    model,
    isNew,
    isNational,
    description,
    quantity,
    code,
  } = formData;

  const [autocompleteChosen, setAutocompleteChosen] = useState({
    partName: false,
    manufacturer: false,
    brand: false,
    model: false,
  });

  const targetMap = {
    partName: "searchAutopartName",
    manufacturer: "searchManufacturerName",
    brand: "searchBrandName",
    model: "searchModelName",
  };

  const [searchResults, setSearchResults] = useState([]);
  const [inputFocused, setInputFocused] = useState("");
  const [debounceTimeouts, setDebounceTimeouts] = useState({});

  const debounceSearch = (fieldName, text) => {
    if (debounceTimeouts[fieldName]) {
      clearTimeout(debounceTimeouts[fieldName]);
    }

    const timeoutId = setTimeout(async () => {
      if (text.length > 3 && !autocompleteChosen[fieldName]) {
        const target = targetMap[fieldName];
        const data = await controller.autopartCreateRequest(target, text);
        setSearchResults(data.response);
      } else {
        setAutocompleteChosen((prevData) => ({
          ...prevData,
          [fieldName]: false,
        }));
        setSearchResults([]);
      }
    }, 1000); // 1 segundo de espera após o usuário parar de digitar

    setDebounceTimeouts((prevTimeouts) => ({
      ...prevTimeouts,
      [fieldName]: timeoutId,
    }));
  };

  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts).forEach(clearTimeout);
    };
  }, [debounceTimeouts]);

  const handleResultTap = async (fieldName, choice, id = null) => {
    if (id !== null) {
      const productDataResponse = await controller.loadProductData(id);
      if (productDataResponse.success) {
        Object.keys(productDataResponse.response).forEach(key => {
          setFormData((prevData) => {
            if (!prevData[key]) {
              return {
                ...prevData,
                [key]: productDataResponse.response[key],
              };
            }
            return prevData;
          });
        });
      }
    }
    setAutocompleteChosen((prevData) => ({
      ...prevData,
      [fieldName]: true,
    }));
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: choice,
    }));
    setSearchResults([]);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setSelectedImages(selectedFiles);

    const previews = selectedFiles.map((file) => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  useEffect(() => {
    return () => {
      imagePreviews.forEach((preview) => URL.revokeObjectURL(preview));
    };
  }, [imagePreviews]);

  const calculateTimeDifference = (targetDateTime) => {
    const now = new Date();
    const targetDate = new Date(targetDateTime);
    const diffInSeconds = Math.floor((now - targetDate) / 1000);

    if (diffInSeconds < 30) {
      return "Agora";
    } else if (diffInSeconds < 60) {
      return "Neste minuto";
    } else if (diffInSeconds < 3600) {
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      return `Há ${diffInMinutes} minuto${diffInMinutes === 1 ? "" : "s"}`;
    } else if (diffInSeconds < 86400) {
      const diffInHours = Math.floor(diffInSeconds / 3600);
      return `Há ${diffInHours} hora${diffInHours === 1 ? "" : "s"}`;
    } else if (diffInSeconds < 604800) {
      const diffInDays = Math.floor(diffInSeconds / 86400);
      return `Há ${diffInDays} dia${diffInDays === 1 ? "" : "s"}`;
    } else if (diffInSeconds < 2629743) {
      const diffInWeeks = Math.floor(diffInSeconds / 604800);
      return `Há ${diffInWeeks} semana${diffInWeeks === 1 ? "" : "s"}`;
    } else {
      const diffInMonths = Math.floor(diffInSeconds / 2629743);
      return `Há ${diffInMonths} mês${diffInMonths === 1 ? "" : "es"}`;
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setProccessing(true);
    const newNumber = e.target["price"].value
      .replace("R$ ", "")
      .replaceAll(".", "")
      .replace(",", "");
    const priceFloat = parseFloat(newNumber / 100);

    const submitedFormData = new FormData();
    submitedFormData.append("part_name", partName);
    submitedFormData.append("manufacturer", manufacturer);
    submitedFormData.append("brand", brand);
    submitedFormData.append("model", model);
    submitedFormData.append("is_new", isNew ? 1 : 0);
    submitedFormData.append("is_national", isNational ? 1 : 0);
    submitedFormData.append("description", description);
    submitedFormData.append("price", priceFloat);
    submitedFormData.append("quantity", quantity);
    submitedFormData.append("code", code);

    selectedImages.forEach((file, index) => {
      submitedFormData.append(`image${index}`, file);
    });
    try {
      const response = await controller.onProductSubmit(submitedFormData);
      if (response.success === true) {
        navigate("/adm/productList", {
          state: {
            showToast: true,
            text: translate.created,
            image: response.coverImage,
            date: new Date().toISOString(),
          },
        });
      } else {
        setProccessing(false);
        if (response.description) {
          if (Array.isArray(response.description)) {
            let errorDescriptions = "";
            Object.keys(response.description).forEach((key) => {
              if (Array.isArray(response.description[key])) {
                response.description[key].forEach((errorMessage) => {
                  errorDescriptions += `${errorMessage} `;
                });
              }
            });
            setToasting(errorDescriptions);
          } else {
            setToasting(response.description);
          }
        } else {
          setToasting(response);
        }
      }
    } catch (error) {
      if (error === "401") {
        setCallbackLogin(
          {
            goBack: true,
            origin: '/adm/dashboard',
          }
        );
        handleLogout();
        navigate('/auth');
      }
    }
  };

  const content = (
    <>
      <h4 className="mb-4">{translate.title}</h4>
      <Card>
        <Card.Body>
          <p className="text-muted mb-3">{translate.subtitle_one}</p>
          <Form onSubmit={handleFormSubmit}>
            <fieldset disabled={isProccessing}>
              <FloatingLabel
                className="mb-3"
                controlId="floatingInputGrid"
                label={translate.form.part_name.label}
              >
                <Form.Control
                  type="text"
                  placeholder={translate.form.part_name.placeholder}
                  className="text-capitalize"
                  required
                  autoComplete="off"
                  aria-describedby="partNameHelpBlock"
                  value={partName}
                  onChange={(e) => {
                    setFormData((prevData) => ({
                      ...prevData,
                      partName: e.target.value,
                    }));
                    debounceSearch("partName", e.target.value);
                  }}
                  onFocus={() => {
                    setSearchResults([]);
                    setInputFocused("partName");
                  }}
                />
              </FloatingLabel>
              <Form.Text className="d-none" id="partNameHelpBlock">
                Neste campo você informa o nome da peça. Ao concluir pressione
                TAB para continuar.
              </Form.Text>
              {searchResults.length > 0 && inputFocused === "partName" && (
                <ul
                  className={`search-results ${searchResults.length > 0 ? "active" : ""
                    }`}
                >
                  {searchResults.map((result, index) => (
                    <li
                      key={index}
                      onClick={() => handleResultTap("partName", result.part_name, result.id)}
                    >
                      {result.part_name}
                    </li>
                  ))}
                </ul>
              )}
              <Form.Group className="mb-3" controlId="code">
                <FloatingLabel
                  className="mb-3"
                  controlId="floatingInputGrid"
                  label={translate.form.code.label}
                >
                  <Form.Control
                    type="text"
                    placeholder={translate.form.code.placeholder}
                    autoComplete="off"
                    aria-describedby="codeHelpBlock"
                    value={code}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        code: e.target.value,
                      }));
                    }}
                    onFocus={() => {
                      setSearchResults([]);
                      setInputFocused("code");
                    }}
                  />
                </FloatingLabel>
              </Form.Group>
              <Form.Text className="d-none" id="codeHelpBlock">
                Neste campo você informa o código do produto, como um código de barras ou outro código que identifica este produto como único.
              </Form.Text>

              <Form.Group className="mb-3" controlId="manufacturer">
                <FloatingLabel
                  className="mb-3"
                  controlId="floatingInputGrid"
                  label={translate.form.manufacturer.label}
                >
                  <Form.Control
                    type="text"
                    placeholder={translate.form.manufacturer.placeholder}
                    className="text-capitalize"
                    required
                    autoComplete="off"
                    aria-describedby="manufacturerHelpBlock"
                    value={manufacturer}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        manufacturer: e.target.value,
                      }));
                      debounceSearch("manufacturer", e.target.value);
                    }}
                    onFocus={() => {
                      setSearchResults([]);
                      setInputFocused("manufacturer");
                    }}
                  />
                </FloatingLabel>
              </Form.Group>
              <Form.Text className="d-none" id="manufacturerHelpBlock">
                Neste campo você informa o nome do fabricante da peça. Ao
                concluir pressione TAB para continuar.
              </Form.Text>
              {searchResults.length > 0 && inputFocused === "manufacturer" && (
                <ul
                  className={`search-results ${searchResults.length > 0 ? "active" : ""
                    }`}
                >
                  {searchResults.map((result, index) => (
                    <li
                      key={index}
                      onClick={() => handleResultTap("manufacturer", result)}
                    >
                      {result}
                    </li>
                  ))}
                </ul>
              )}

              <CurrencyInput
                showPrefix
                placeholder={translate.form.price.placeholder}
                onChange={setPrice}
                value={price}
              >
                {(inputProps) => (

                  <FloatingLabel
                    className="mb-3"
                    label={translate.form.price.label}
                  >
                    <Form.Control
                      {...inputProps}
                      type="tel"
                      className=""
                      autoComplete="off"
                      aria-describedby="priceHelpBlock"
                      required
                      onFocus={() => setSearchResults([])}
                      id="price"
                    />
                  </FloatingLabel>
                )}
              </CurrencyInput>
              <Form.Text className="d-none" id="priceHelpBlock">
                Informe o valor da peça, o campo preenche automaticamente a
                vírgula dos centavos e os pontos de milhar. Ao concluir
                pressione TAB para continuar.
              </Form.Text>

              <FloatingLabel
                className="mb-3"
                controlId="floatingInputGrid"
                label={translate.form.quantity.label}
              >
                <Form.Control
                  controlId="quantity"
                  type="number"
                  placeholder={translate.form.quantity.placeholder}
                  min={1}
                  className="mt-3"
                  required
                  aria-describedby="quantityHelpBlock"
                  onFocus={() => setSearchResults([])}
                  onChange={(e) => {
                    setFormData((prevData) => ({
                      ...prevData,
                      quantity: e.target.value,
                    }));
                  }}
                  style={{ width: 100 }}
                />
              </FloatingLabel>
              <Form.Text className="d-none" id="quantityHelpBlock">
                Informe quantos itens desse você tem disponíveis para venda. Ao
                concluir pressione TAB para continuar.
              </Form.Text>

              <Form.Group className="mt-3" controlId="description">
                <Form.Control
                  as="textarea"
                  style={{ height: '150px' }}
                  placeholder={translate.form.description.placeholder}
                  aria-describedby="descriptionHelpBlock"
                  required
                  onChange={(e) => {
                    setFormData((prevData) => ({
                      ...prevData,
                      description: e.target.value,
                    }));
                  }}
                  value={description}
                />
              </Form.Group>
              <Form.Text className="d-none" id="descriptionHelpBlock">
                Descreva detalhes dessa peça, inclusive se tem arranhões, se é
                nova ou usada... Ao concluir pressione TAB para continuar.
              </Form.Text>

              <p className="text-muted mb-3 mt-5">{translate.subtitle_two}</p>

              <Form.Group className="mb-3" controlId="brand">
                <FloatingLabel
                  className="mb-3"
                  controlId="floatingInputGrid"
                  label={translate.form.brand.placeholder}
                >
                  <Form.Control
                    type="text"
                    placeholder={translate.form.brand.placeholder}
                    className="text-capitalize"
                    required
                    aria-describedby="brandHelpBlock"
                    autoComplete="off"
                    value={brand}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        brand: e.target.value,
                      }));
                      debounceSearch("brand", e.target.value);
                    }}
                    onFocus={() => {
                      setSearchResults([]);
                      setInputFocused("brand");
                    }}
                  />
                </FloatingLabel>
              </Form.Group>
              <Form.Text className="d-none" id="brandHelpBlock">
                Informe a qual marca de veículo esta peça atende. Ao concluir
                pressione TAB para continuar.
              </Form.Text>
              {searchResults.length > 0 && inputFocused === "brand" && (
                <ul
                  className={`search-results ${searchResults.length > 0 ? "active" : ""
                    }`}
                >
                  {searchResults.map((result, index) => (
                    <li
                      key={index}
                      onClick={() => handleResultTap("brand", result)}
                    >
                      {result}
                    </li>
                  ))}
                </ul>
              )}

              <Form.Group className="mb-3" controlId="model">
                <FloatingLabel
                  className="mb-3"
                  controlId="floatingInputGrid"
                  label={translate.form.model.placeholder}
                >
                  <Form.Control
                    type="text"
                    placeholder={translate.form.model.placeholder}
                    className="text-capitalize"
                    required
                    autoComplete="off"
                    aria-describedby="modelHelpBlock"
                    value={model}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        model: e.target.value,
                      }));
                      debounceSearch("model", e.target.value);
                    }}
                    onFocus={() => {
                      setSearchResults([]);
                      setInputFocused("model");
                    }}
                  />
                </FloatingLabel>
              </Form.Group>
              <Form.Text className="d-none" id="modelHelpBlock">
                Informe a qual modelo de veículo esta peça atende. Ao concluir
                pressione TAB para continuar.
              </Form.Text>
              {searchResults.length > 0 && inputFocused === "model" && (
                <ul
                  className={`search-results ${searchResults.length > 0 ? "active" : ""
                    }`}
                >
                  {searchResults.map((result, index) => (
                    <li
                      key={index}
                      onClick={() => handleResultTap("model", result)}
                    >
                      {result}
                    </li>
                  ))}
                </ul>
              )}

              <Form.Check
                type="switch"
                id="custom-switch"
                label={translate.form.isNew}
                onChange={(e) => {
                  setFormData((prevData) => ({
                    ...prevData,
                    isNew: e.target.checked,
                  }));
                }}
                checked={formData.isNew}
              />

              <Form.Check
                type="switch"
                id="custom-switch"
                label={translate.form.model.national}
                onChange={(e) => {
                  setFormData((prevData) => ({
                    ...prevData,
                    isNational: e.target.checked,
                  }));
                }}
                checked={formData.isNational}
              />

              <p className="text-muted mb-3 mt-5">{translate.subtitle_three}</p>

              <Form.Group controlId="formFileMultiple" className="mb-3">
                <Form.Label>{translate.form.image.label}</Form.Label>
                <Form.Control
                  type="file"
                  accept=".jpg, .jpeg, .png, .jfif, .webp"
                  multiple
                  required
                  onChange={handleFileChange}
                  onFocus={() => setSearchResults([])}
                />
              </Form.Group>
              <Row className="mb-5">
                <Col>
                  {imagePreviews.map((preview, index) => (
                    <Image
                      key={index}
                      src={preview}
                      alt={`Preview ${index}`}
                      style={{ maxWidth: 100, maxHeight: 100 }}
                      thumbnail
                    />
                  ))}
                </Col>
              </Row>
              <Button variant="primary" type="submit">
                Gravar
              </Button>
            </fieldset>
          </Form>
        </Card.Body>
      </Card>
      <UserDataPanelWrapper
        showModal={userData.progress === "incomplete"}
        setShowModal={() => false}
      />
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1 }}>
        <Toast
          bg={"danger"}
          onClose={() => setToasting(null)}
          show={toasting !== null ? true : false}
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Montacar</strong>
            <small>
              {toasting !== null
                ? calculateTimeDifference(new Date().toISOString())
                : "-"}
            </small>
          </Toast.Header>
          <Toast.Body className={"text-white"}>
            {toasting !== null ? toasting : null}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
  return <AdmPanelContainer children={content} />;
}
